// Onboarding Session Management

export interface OnboardingSession {
  sessionId: string;
  employeeId: string;
  employeeData: any;
  collectionId?: string;
  executionId?: string;
  uploadedFiles: {
    id: string;
    fileName: string;
    documentId?: string;
    status: 'pending' | 'uploaded' | 'processing' | 'completed' | 'failed';
  }[];
  status: 'active' | 'paused' | 'completed';
  createdAt: string;
  lastUpdated: string;
  expiresAt: string; // 24 hours from creation
}

const SESSION_KEY = 'turfai_onboarding_session';
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export class OnboardingSessionManager {
  static createSession(employeeId: string, employeeData: any): OnboardingSession {
    const now = new Date();
    const session: OnboardingSession = {
      sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      employeeId,
      employeeData,
      uploadedFiles: [],
      status: 'active',
      createdAt: now.toISOString(),
      lastUpdated: now.toISOString(),
      expiresAt: new Date(now.getTime() + SESSION_DURATION).toISOString()
    };
    
    this.saveSession(session);
    return session;
  }

  static getSession(): OnboardingSession | null {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (!stored) return null;
      
      const session = JSON.parse(stored) as OnboardingSession;
      
      // Check if session has expired
      if (new Date(session.expiresAt) < new Date()) {
        this.clearSession();
        return null;
      }
      
      return session;
    } catch (error) {
      console.error('Error retrieving session:', error);
      return null;
    }
  }

  static saveSession(session: OnboardingSession): void {
    try {
      session.lastUpdated = new Date().toISOString();
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('Error saving session:', error);
    }
  }

  static updateSession(updates: Partial<OnboardingSession>): OnboardingSession | null {
    const session = this.getSession();
    if (!session) return null;
    
    const updatedSession = {
      ...session,
      ...updates,
      lastUpdated: new Date().toISOString()
    };
    
    this.saveSession(updatedSession);
    return updatedSession;
  }

  static addUploadedFile(fileInfo: {
    id: string;
    fileName: string;
    documentId?: string;
    status: 'pending' | 'uploaded' | 'processing' | 'completed' | 'failed';
  }): void {
    const session = this.getSession();
    if (!session) return;
    
    const existingIndex = session.uploadedFiles.findIndex(f => f.id === fileInfo.id);
    if (existingIndex >= 0) {
      session.uploadedFiles[existingIndex] = fileInfo;
    } else {
      session.uploadedFiles.push(fileInfo);
    }
    
    this.saveSession(session);
  }

  static setCollectionId(collectionId: string): void {
    this.updateSession({ collectionId });
  }

  static setExecutionId(executionId: string): void {
    this.updateSession({ executionId });
  }

  static pauseSession(): void {
    this.updateSession({ status: 'paused' });
  }

  static resumeSession(): void {
    this.updateSession({ status: 'active' });
  }

  static completeSession(): void {
    this.updateSession({ status: 'completed' });
  }

  static clearSession(): void {
    localStorage.removeItem(SESSION_KEY);
  }

  static canResumeSession(): boolean {
    const session = this.getSession();
    return session !== null && session.status !== 'completed';
  }
}